/**
 * WorkerNatsSeriesProvider — live NATS data via a dedicated Web Worker.
 *
 * The shared, reliable live path for every condition-monitoring screen. It runs
 * the NATS connection + JetStream consumers off the main thread on their own
 * connection (see natsLive.worker.ts), which is what lets it cope with large
 * edge servers (edge-16) where a main-thread ordered consumer stalls. Channel
 * resolution stays here so the worker remains channel-agnostic; panels are
 * unaffected — they only see the {@link SeriesProvider} interface.
 */

import NatsLiveWorker from './natsLive.worker.ts?worker';
import type { RawPoint } from './natsLive.worker';
import { natsClient } from '../../services/NATSClient';
import { getValidToken } from '../../auth/tokenManager';
import { getOrgAlias, getOrgStreamName } from '../../utils/tokenUtils';
import { resolveSubjectChannel } from '../channelResolver';
import type {
  ProviderContext,
  SeriesProvider,
  SeriesPatch,
  SeriesPatchHandler,
  SeriesSubscription,
} from './SeriesProvider';

type WorkerOut =
  | { type: 'CONNECTED' }
  | { type: 'DATA'; points: RawPoint[]; snapshot: boolean }
  | { type: 'ERROR'; error: string }
  | { type: 'DISCONNECTED' };

export class WorkerNatsSeriesProvider implements SeriesProvider {
  readonly mode = 'nats' as const;

  open(ctx: ProviderContext, _range: unknown, onPatch: SeriesPatchHandler): SeriesSubscription {
    let cancelled = false;
    let worker: Worker | null = null;

    const start = async (): Promise<void> => {
      if (!ctx.machineBK) {
        console.warn('WorkerNatsSeriesProvider: no machineBK — not subscribing');
        return;
      }
      let token: string;
      let streamName: string;
      let currentStreamName: string;
      try {
        token = await getValidToken();
        const orgAlias = getOrgAlias(token);
        if (!orgAlias) {
          console.warn('WorkerNatsSeriesProvider: no org alias in token');
          return;
        }
        const baseStream = getOrgStreamName(orgAlias);
        streamName = `${baseStream}_LIVE`;
        currentStreamName = `${baseStream}_CURRENT`;
      } catch (e) {
        console.warn('WorkerNatsSeriesProvider: token resolution failed', e);
        return;
      }
      if (cancelled) return;

      const serverUrl = import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443';
      const subject = `edge-data.live.*.${ctx.machineBK}.>`;
      // Always-retained last value for the initial snapshot (KPIs).
      const currentSubject = `edge-data.current.*.${ctx.machineBK}.>`;

      worker = new NatsLiveWorker();
      worker.onmessage = (event: MessageEvent<WorkerOut>) => {
        if (cancelled) return;
        const msg = event.data;
        switch (msg.type) {
          case 'CONNECTED':
            console.log(`WorkerNatsSeriesProvider: connected — ${subject}`);
            break;
          case 'DATA': {
            const patch = resolvePatch(msg.points, ctx, msg.snapshot);
            if (patch) onPatch(patch);
            break;
          }
          case 'ERROR':
            console.error('WorkerNatsSeriesProvider: worker error', msg.error);
            break;
          case 'DISCONNECTED':
            break;
        }
      };
      worker.onerror = (err) => console.error('WorkerNatsSeriesProvider: worker crashed', err);

      worker.postMessage({
        type: 'CONNECT',
        serverUrl,
        token,
        subject,
        streamName,
        currentSubject,
        currentStreamName,
      });
    };

    // Wait until the main NATS client reports connectivity before spawning, so
    // we don't race auth/startup (the worker uses its own connection but the
    // same token/servers). Retry briefly if not yet connected.
    const waitAndStart = (attempt = 0): void => {
      if (cancelled) return;
      if (natsClient.isConnected() || attempt > 10) {
        void start();
      } else {
        setTimeout(() => waitAndStart(attempt + 1), 1000);
      }
    };
    waitAndStart();

    return {
      close() {
        cancelled = true;
        if (worker) {
          try { worker.postMessage({ type: 'DISCONNECT' }); } catch { /* */ }
          const dying = worker;
          worker = null;
          setTimeout(() => dying.terminate(), 300);
        }
      },
    };
  }
}

/** Group raw worker points into a channel-keyed patch, or null if none matched. */
function resolvePatch(points: RawPoint[], ctx: ProviderContext, snapshot: boolean): SeriesPatch | null {
  const byChannel: Record<string, { t: number; v: number }[]> = {};
  const latest: Record<string, number> = {};
  for (const [subject, v, t] of points) {
    const channel = resolveSubjectChannel(subject, ctx.channels);
    if (!channel) continue;
    (byChannel[channel] ??= []).push({ t, v });
    latest[channel] = v;
  }
  if (Object.keys(byChannel).length === 0) return null;
  // Snapshot points can arrive out of subject order; keep each channel sorted.
  for (const key of Object.keys(byChannel)) byChannel[key].sort((a, b) => a.t - b.t);
  return { byChannel, latest, loading: false, ...(snapshot ? {} : {}) };
}
